import {
  collection, addDoc, doc, deleteDoc, getDocs, setDoc,
} from 'firebase/firestore';
import {
  ref as storageRef, uploadBytes, getDownloadURL, deleteObject,
} from 'firebase/storage';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { db, storage, secondaryAuth } from '../firebase/firebase';
import {
  BRANCH_COLLECTION, flattenBranchDocs, toNestedBranch,
} from '../utills/branchAdapter';

export interface Store {
  image: string;
  name: string;
}

export interface BranchModel {
  announcement: string;
  bannerImages: string[];
  branchId: string;
  delivery: number;
  email: string;
  image?: string;
  kmcharge: number;
  minAmount: number;
  online: boolean;
  active: boolean;
  bannersEnabled: boolean;
  bannerAutoCycle: boolean;
  bannerShowDots: boolean;
  bannerCycleSeconds: number;
  weatherEnabled: boolean;
  weather: string;
  weatherSwapDelaySeconds: number;
  packing: number;
  phone: string;
  pincode: string;
  policy: string;
  radius: string;
  range: number;
  service: number;
  store: Store[];
  storeLat: string;
  storeLon: string;
  storecate: string;
  storename: string;
  storeuid: string;
  tax: number;
  timestamp: string;
  uid: string;
}

const COL = BRANCH_COLLECTION;
const branchCollection = collection(db, COL);

// ── Storage helpers ──────────────────────────────────────────────

export async function uploadBranchImage(file: File, uid: string): Promise<string> {
  const path = `branches/${uid}_${file.name}`;
  const imgRef = storageRef(storage, path);
  await uploadBytes(imgRef, file);
  return getDownloadURL(imgRef);
}

export async function uploadBranchBanner(file: File, uid: string, index: number): Promise<string> {
  const path = `branches/banners/${uid}_${index}_${file.name}`;
  const imgRef = storageRef(storage, path);
  await uploadBytes(imgRef, file);
  return getDownloadURL(imgRef);
}

export async function deleteStorageUrl(url: string): Promise<void> {
  if (!url || !url.includes('firebasestorage')) return;
  try {
    const match = url.match(/\/o\/(.+?)\?/);
    if (!match) return;
    await deleteObject(storageRef(storage, decodeURIComponent(match[1])));
  } catch (e) {
    console.warn('Storage delete skipped:', e);
  }
}

// ── CRUD ─────────────────────────────────────────────────────────

/** Register branch with Firebase Auth (secondary app) + Firestore */
export const registerBranch = async (
  email: string,
  password: string,
  branchData: Omit<BranchModel, 'uid' | 'timestamp' | 'email'>,
): Promise<string> => {
  const credential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
  const uid = credential.user.uid;
  await signOut(secondaryAuth);

  await setDoc(doc(db, COL, uid), {
    ...toNestedBranch({ ...branchData, branchId: (branchData as any).branchId || uid }, true),
    uid,
    email,
    createdAt: new Date().toISOString(),
  });
  return uid;
};

// Fetch all branch documents (flattened to the BranchModel shape)
export const fetchBranches = async (): Promise<BranchModel[]> => {
  try {
    const snapshot = await getDocs(branchCollection);
    return flattenBranchDocs(snapshot.docs) as BranchModel[];
  } catch (error) {
    console.error('Failed to fetch branches:', error);
    return [];
  }
};

// Update branch by uid (deep-merge so untouched nested sub-fields survive)
export const updateBranch = async (uid: string, updateFields: Partial<BranchModel>) => {
  try {
    const docRef = doc(branchCollection, uid);
    await setDoc(docRef, { ...toNestedBranch(updateFields), updatedAt: new Date().toISOString() }, { merge: true });
  } catch (error) {
    console.error('Failed to update branch:', error);
    throw error;
  }
};

// Delete branch by uid (also cleans Storage)
export const deleteBranch = async (uid: string, image?: string, bannerImages?: string[]) => {
  try {
    await deleteDoc(doc(branchCollection, uid));
    if (image) await deleteStorageUrl(image);
    if (bannerImages) {
      await Promise.all(bannerImages.map(url => deleteStorageUrl(url)));
    }
  } catch (error) {
    console.error('Failed to delete branch:', error);
    throw error;
  }
};

// Legacy createBranch kept for compatibility
export const createBranch = async (branchData: Omit<BranchModel, 'uid' | 'timestamp'>) => {
  try {
    const docRef = await addDoc(branchCollection, {
      ...toNestedBranch(branchData, true),
      createdAt: new Date().toISOString(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Failed to create branch:', error);
    throw error;
  }
};
